import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { NavLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { DataTable, DataTableColumn, EmptyState, Notice, PageHeader, StatusChip } from '@routes-plan/ui';
import StandardModal, { useModalLoading } from '../../../components/modales/StandardModal';
import ProductForm from './ProductForm';
import ProductAssignments from './ProductAssignments';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import {
  INVENTORY_ROUTES,
  OfficeOption,
  Product,
  RouteOption,
  ShowAlerts,
  apiErrorMessage,
  fetchOffices,
  fetchRoutes,
  formatMoney,
  utilityPercentOf,
} from '../shared/inventoryApi';
import { ActionIcon, CompanyMoneyField, FilterGrid, PageBody, SaveButton, useInventoryAlert } from '../shared/ui';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import { companyLocale } from '../../../utils/format/currency';

type Row = Product & {
  utilityPct: number | null;
  utilityAmount: number | null;
  hasStock: boolean;
  stockHint: string;
  expirationLabel: string;
  officeLabel: string;
};

/** Catálogo de productos de cada sede y asignación de rutas a varios productos a la vez. */
const ProductList: React.FC<ShowAlerts> = ({ showLoading, hideLoading }) => {
  const t = useT();
  const showAlert = useInventoryAlert();
  const { canCreate, canUpdate } = useResourcePermissions(INVENTORY_ROUTES.products);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<number[]>([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [offices, setOffices] = useState<OfficeOption[]>([]);
  const [officeFilter, setOfficeFilter] = useState<number | ''>('');
  const [officesLoaded, setOfficesLoaded] = useState(false);
  // Editar y Asignar rutas se abren en el modal estándar sobre el listado.
  const [modal, setModal] = useState<{ kind: 'edit' | 'assign'; id: number; title: string } | null>(null);
  const editLoading = useModalLoading();
  // Crear producto: asistente en el modal estándar (createKey reinicia el asistente para "Crear otro").
  const [createOpen, setCreateOpen] = useState(false);
  const [createKey, setCreateKey] = useState(0);
  const createLoading = useModalLoading();
  const assignLoading = useModalLoading();
  setPageTitle('menu.inventory.products');

  useEffect(() => {
    fetchOffices()
      .then(setOffices)
      .catch(() => setOffices([]))
      .finally(() => setOfficesLoaded(true));
  }, []);

  const load = useCallback(async () => {
    if (!officesLoaded) return;
    setLoading(true);
    try {
      const { data } = await axios.get('products', { params: officeFilter ? { officeId: officeFilter } : {} });
      const list: Product[] = Array.isArray(data?.data) ? data.data : [];
      // Solo productos de sedes de objeto real (o antiguos sin sede, para asignarles una).
      const realIds = new Set(offices.map((o) => o.id));
      setProducts(list.filter((p) => p.officeId == null || realIds.has(Number(p.officeId))));
    } catch (error) {
      showAlert(apiErrorMessage(error, t('products.loadError')), 'error');
    } finally {
      setLoading(false);
    }
  }, [showAlert, officeFilter, offices, officesLoaded, t]);

  useEffect(() => {
    load();
  }, [load]);

  const rows: Row[] = useMemo(
    () =>
      products.map((p) => {
        const cost = Number(p.costPrice || 0);
        const r = p.readiness;
        const hasStock = Boolean(r && (r.routesWithStock > 0 || r.officeStock > 0));
        const stockHint = !r
          ? t('products.list.stockNoInfo')
          : hasStock
            ? [
                r.routesWithStock > 0
                  ? r.routesWithStock === 1
                    ? t('products.list.stockInRoute')
                    : t('products.list.stockInRoutes', { count: r.routesWithStock })
                  : t('products.list.stockNoRoutes'),
                t('products.list.stockInWarehouse', { count: r.officeStock }),
              ].join(' · ')
            : t('products.list.stockNone');
        return {
          ...p,
          // Utilidad informativa sobre el costo (el precio ya la incluye).
          utilityPct: utilityPercentOf(cost, Number(p.price || 0)),
          utilityAmount: cost > 0 ? Number(p.price) - cost : null,
          hasStock,
          stockHint,
          expirationLabel: p.hasExpiration
            ? p.shelfLifeDays
              ? t('products.list.expiresYesDays', { days: p.shelfLifeDays })
              : t('common.yes')
            : t('common.no'),
          officeLabel: p.office?.name ?? t('products.list.noOffice'),
        };
      }),
    [products, t],
  );

  const toggle = (id: number) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const allSelected = rows.length > 0 && rows.every((r) => selected.includes(r.id));
  const someSelected = selected.length > 0 && !allSelected;

  const openBulk = () => {
    const officeIds = new Set(products.filter((p) => selected.includes(p.id)).map((p) => p.officeId ?? 0));
    if (officeIds.size > 1 || officeIds.has(0)) {
      showAlert(t('products.list.oneOfficeOnly'), 'warning');
      return;
    }
    setBulkOpen(true);
  };

  const money = (v: number | null) => (v === null ? '—' : formatMoney(v));

  const columns: DataTableColumn<Row>[] = [
    {
      id: 'title',
      header: canUpdate ? (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Checkbox
            size="small"
            sx={{ p: 0.5, ml: -0.5 }}
            checked={allSelected}
            indeterminate={someSelected}
            onClick={(e) => e.stopPropagation()}
            onChange={() => setSelected(allSelected ? [] : rows.map((r) => r.id))}
            inputProps={{ 'aria-label': t('products.list.selectAll') }}
          />
          {t('products.product')}
        </Box>
      ) : (
        t('products.product')
      ),
      csvHeader: t('products.product'),
      value: (r) => r.title,
      sortable: true,
      render: (r) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: { xs: 200, md: 320 } }}>
          {canUpdate && (
            <Checkbox
              size="small"
              sx={{ p: 0.5, ml: -0.5 }}
              checked={selected.includes(r.id)}
              onChange={() => toggle(r.id)}
              inputProps={{ 'aria-label': t('products.list.selectOne', { name: r.title }) }}
            />
          )}
          <Typography variant="body2" noWrap title={r.title} sx={{ fontWeight: 500, minWidth: 0 }}>
            {r.title}
          </Typography>
        </Box>
      ),
    },
    ...(offices.length > 1
      ? [{ id: 'office', header: t('common.office'), value: (r: Row) => r.officeLabel, sortable: true, hideBelow: 'md' as const }]
      : []),
    { id: 'sku', header: t('products.sku'), value: (r) => r.sku ?? '', sortable: true, hideBelow: 'sm' },
    { id: 'unit', header: t('products.unit'), value: (r) => r.unit ?? '', hideBelow: 'md' },
    {
      id: 'cost',
      header: t('products.cost'),
      align: 'right',
      sortable: true,
      value: (r) => (Number(r.costPrice) > 0 ? Number(r.costPrice) : null),
      render: (r) => money(Number(r.costPrice) > 0 ? Number(r.costPrice) : null),
      hideBelow: 'sm',
    },
    { id: 'price', header: t('products.price'), align: 'right', sortable: true, value: (r) => Number(r.price), render: (r) => formatMoney(r.price) },
    {
      id: 'utilityPct',
      header: t('products.utilityPercent'),
      align: 'right',
      sortable: true,
      value: (r) => r.utilityPct,
      render: (r) => (r.utilityPct === null ? '—' : `${r.utilityPct.toLocaleString(companyLocale())} %`),
      hideBelow: 'md',
    },
    {
      id: 'utilityAmount',
      header: t('products.utilityAmount'),
      align: 'right',
      sortable: true,
      value: (r) => r.utilityAmount,
      render: (r) => money(r.utilityAmount),
      hideBelow: 'md',
    },
    { id: 'expires', header: t('products.list.expires'), value: (r) => r.expirationLabel, hideBelow: 'md' },
    { id: 'minStock', header: t('products.minStock'), align: 'right', value: (r) => r.minStock ?? null, hideBelow: 'md' },
    {
      id: 'stock',
      header: t('products.list.stock'),
      value: (r) => (r.hasStock ? t('products.list.withStock') : t('products.list.withoutStock')),
      sortable: true,
      render: (r) => (
        <Tooltip title={r.stockHint}>
          <span>
            <StatusChip label={r.hasStock ? t('products.list.withStock') : t('products.list.withoutStock')} tone={r.hasStock ? 'success' : 'warning'} />
          </span>
        </Tooltip>
      ),
    },
    {
      id: 'status',
      header: t('common.status'),
      value: (r) => (r.active === false ? t('common.inactive') : t('common.active')),
      sortable: true,
      render: (r) => <StatusChip label={r.active === false ? t('common.inactive') : t('common.active')} tone={r.active === false ? 'neutral' : 'success'} />,
    },
  ];

  const newButton = (
    <PermissionButton
      allowed={canCreate}
      deniedTooltip={t('common.noPermissionCreate')}
      variant="contained"
      startIcon={<AddOutlinedIcon />}
      onClick={() => {
        setCreateKey((k) => k + 1);
        setCreateOpen(true);
      }}
    >
      {t('products.list.new')}
    </PermissionButton>
  );

  return (
    <PageBody>
      <PageHeader
        title={t('products.list.title')}
        description={t('products.list.description')}
        actions={
          <>
            <Tooltip title={t('products.list.byRouteTooltip')}>
              <Button variant="outlined" startIcon={<FactCheckOutlinedIcon />} component={NavLink} to={`${INVENTORY_ROUTES.products}/by-route`}>
                {t('products.byRoute.title')}
              </Button>
            </Tooltip>
            {newButton}
          </>
        }
      />
      {selected.length > 0 && (
        <Notice
          severity="info"
          action={
            <Stack direction="row" spacing={1}>
              <Button size="small" color="inherit" onClick={() => setSelected([])}>
                {t('products.list.clearSelection')}
              </Button>
              <PermissionButton
                allowed={canUpdate}
                deniedTooltip={t('products.list.noPermissionAssign')}
                size="small"
                variant="contained"
                startIcon={<AltRouteOutlinedIcon />}
                onClick={openBulk}
              >
                {t('products.list.routesButton')}
              </PermissionButton>
            </Stack>
          }
        >
          {selected.length === 1 ? t('products.list.selectedOne') : t('products.list.selectedMany', { count: selected.length })}
        </Notice>
      )}
      <Card>
        <CardContent>
          {offices.length > 1 && (
            <FilterGrid sx={{ mb: 2, maxWidth: { md: 400 } }}>
              <TextField
                select
                size="small"
                fullWidth
                label={t('common.office')}
                value={officeFilter === '' ? '' : String(officeFilter)}
                onChange={(e) => {
                  setOfficeFilter(Number(e.target.value) || '');
                  setSelected([]);
                }}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value="">{t('products.list.allMyOffices')}</MenuItem>
                {offices.map((o) => (
                  <MenuItem key={o.id} value={String(o.id)}>
                    {o.name}
                  </MenuItem>
                ))}
              </TextField>
            </FilterGrid>
          )}
          <DataTable<Row>
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            loading={loading}
            ariaLabel={t('products.list.title')}
            searchPlaceholder={t('products.searchPlaceholder')}
            exportFileName={t('products.list.exportFileName')}
            initialSort={{ columnId: 'title', direction: 'asc' }}
            rowActions={(r) => (
              <>
                <ActionIcon
                  label={t('common.edit')}
                  icon={<EditOutlinedIcon fontSize="small" />}
                  allowed={canUpdate}
                  deniedTooltip={t('common.noPermissionEdit')}
                  onClick={() => setModal({ kind: 'edit', id: r.id, title: r.title })}
                />
                <ActionIcon
                  label={t('products.assignRoute')}
                  icon={<AltRouteOutlinedIcon fontSize="small" />}
                  allowed={canUpdate}
                  deniedTooltip={t('products.noPermissionAssignRoutes')}
                  onClick={() => setModal({ kind: 'assign', id: r.id, title: r.title })}
                />
              </>
            )}
            emptyState={
              <EmptyState
                icon={<Inventory2OutlinedIcon fontSize="inherit" />}
                title={t('products.list.empty')}
                description={t('products.list.emptyHint')}
                action={canCreate ? newButton : undefined}
              />
            }
          />
        </CardContent>
      </Card>
      {bulkOpen && (
        <BulkAssignDialog
          productIds={selected}
          officeId={Number(products.find((p) => p.id === selected[0])?.officeId || 0)}
          onClose={(saved) => {
            setBulkOpen(false);
            if (saved) {
              setSelected([]);
              load();
            }
          }}
          showAlert={showAlert}
          showLoading={showLoading}
          hideLoading={hideLoading}
        />
      )}
      <StandardModal open={createOpen} onClose={() => setCreateOpen(false)} title={t('products.newTitle')} maxWidth={900} loading={createLoading.loading} composed>
        {createOpen && (
          <ProductForm
            key={`create-${createKey}`}
            variant="modal"
            onClose={() => setCreateOpen(false)}
            onCreated={() => load()}
            onCreateAnother={() => setCreateKey((k) => k + 1)}
            onOpenAssignments={(id) => {
              setCreateOpen(false);
              if (id) setModal({ kind: 'assign', id, title: products.find((p) => p.id === id)?.title ?? '' });
            }}
            showAlert={showAlert}
            showLoading={createLoading.showLoading}
            hideLoading={createLoading.hideLoading}
          />
        )}
      </StandardModal>
      <StandardModal
        open={modal?.kind === 'edit'}
        onClose={() => setModal(null)}
        title={t('products.editTitle')}
        subtitle={modal?.title}
        maxWidth={900}
        loading={editLoading.loading}
        composed
      >
        {modal?.kind === 'edit' && (
          <ProductForm
            key={`edit-${modal.id}`}
            variant="modal"
            productId={modal.id}
            onClose={() => setModal(null)}
            onSaved={() => {
              setModal(null);
              load();
            }}
            onOpenAssignments={() => setModal({ ...modal, kind: 'assign' })}
            showAlert={showAlert}
            showLoading={editLoading.showLoading}
            hideLoading={editLoading.hideLoading}
          />
        )}
      </StandardModal>
      <StandardModal
        open={modal?.kind === 'assign'}
        onClose={() => setModal(null)}
        title={t('products.assignRoute')}
        subtitle={modal?.title}
        maxWidth={1000}
        loading={assignLoading.loading}
        composed
      >
        {modal?.kind === 'assign' && (
          <ProductAssignments
            key={`assign-${modal.id}`}
            variant="modal"
            productId={modal.id}
            onClose={() => setModal(null)}
            onChanged={load}
            showAlert={showAlert}
            showLoading={assignLoading.showLoading}
            hideLoading={assignLoading.hideLoading}
          />
        )}
      </StandardModal>
    </PageBody>
  );
};

interface BulkAssignDialogProps extends ShowAlerts {
  productIds: number[];
  officeId: number;
  onClose: (saved: boolean) => void;
}

/** Para varios productos de una sede: en qué rutas se prestan (o no) y con qué precio. */
const BulkAssignDialog: React.FC<BulkAssignDialogProps> = ({ productIds, officeId, onClose, showAlert }) => {
  const t = useT();
  // El Cargando general queda detrás del modal: se usa el del modal.
  const modalLoading = useModalLoading();
  const { canUpdate } = useResourcePermissions(INVENTORY_ROUTES.products);
  const [routes, setRoutes] = useState<RouteOption[]>([]);
  const [routeIds, setRouteIds] = useState<number[]>([]);
  const [price, setPrice] = useState('');
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [routeError, setRouteError] = useState(false);

  useEffect(() => {
    if (!officeId) return;
    fetchRoutes(officeId).then(setRoutes).catch(() => setRoutes([]));
  }, [officeId]);

  const save = async () => {
    if (!routeIds.length) {
      setRouteError(true);
      return;
    }
    setSaving(true);
    modalLoading.showLoading();
    try {
      const { data } = await axios.post('product-assignments/bulk', {
        productIds: productIds,
        officeIds: [],
        routeIds: routeIds,
        active,
        ...(active && price.trim() ? { price: Number(price) } : {}),
      });
      showAlert(t('products.bulk.saved', { count: data?.assignments ?? 0 }), 'success');
      onClose(true);
    } catch (error) {
      showAlert(apiErrorMessage(error, t('products.bulk.saveError')), 'error');
    } finally {
      setSaving(false);
      modalLoading.hideLoading();
    }
  };

  return (
    <StandardModal
      open
      loading={modalLoading.loading}
      onClose={() => onClose(false)}
      disableClose={saving}
      title={productIds.length === 1 ? t('products.bulk.titleOne') : t('products.bulk.titleMany', { count: productIds.length })}
      maxWidth={600}
      actions={
        <>
          <Button onClick={() => onClose(false)} color="inherit" variant="outlined">
            {t('common.cancel')}
          </Button>
          <SaveButton allowed={canUpdate} deniedTooltip={t('products.noPermissionAssignRoutes')} loading={saving} onClick={save}>
            {t('common.save')}
          </SaveButton>
        </>
      }
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('products.bulk.intro')}
        </Typography>
        <RadioGroup value={active ? 'yes' : 'no'} onChange={(e) => setActive(e.target.value === 'yes')} row>
          <FormControlLabel value="yes" control={<Radio />} label={t('products.bulk.lends')} />
          <FormControlLabel value="no" control={<Radio />} label={t('products.bulk.notLends')} />
        </RadioGroup>
        <TextField
          select
          fullWidth
          required
          label={t('common.routes')}
          value={routeIds}
          error={routeError}
          helperText={routeError ? t('products.bulk.selectRoute') : t('products.bulk.routesHelp')}
          SelectProps={{
            multiple: true,
            renderValue: (ids) =>
              (ids as number[]).map((id) => routes.find((c) => c.id === id)?.name).filter(Boolean).join(', '),
          }}
          onChange={(e) => {
            const v = e.target.value as unknown as number[] | string;
            setRouteIds((typeof v === 'string' ? v.split(',') : v).map(Number));
            setRouteError(false);
          }}
        >
          {routes.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              <Checkbox size="small" checked={routeIds.includes(c.id)} sx={{ py: 0 }} />
              <ListItemText primary={c.name} />
            </MenuItem>
          ))}
        </TextField>
        {active && (
          <CompanyMoneyField
            fullWidth
            label={t('products.bulk.specialPriceOptional')}
            helperText={t('products.bulk.specialPriceHelp')}
            value={price}
            onChange={setPrice}
          />
        )}
      </Stack>
    </StandardModal>
  );
};

export default ProductList;
